import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useStore } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Constants from 'expo-constants';
import { QualityWarningsClient, QualityWarningSummaryDto } from '../api/api.g';
import { LoggedState } from '../models/AuthModelsCommon';
import { createQualityWarningSession, WarningSession } from '../helpers/qualityWarningSession';
import { lightBoxActions } from '../store/lightBoxSlice';
import type { RootState } from '../store/store';
import { useAppDispatch, useAppSelector } from './store-hook';
import useApi from './api-hook';
import QualityWarningPopup from '../components/common/qualityWarning/QualityWarningPopup';

interface QualityWarningContextValue {
  sessionKey?: string;
  warning?: QualityWarningSummaryDto;
  refresh(): Promise<QualityWarningSummaryDto | undefined>;
  acknowledge(id: string): Promise<QualityWarningSummaryDto | undefined>;
  displayed(id: string): void;
  open(): void;
  beginReview(): () => void;
}

const Context = createContext<QualityWarningContextValue>({ refresh: async () => undefined, acknowledge: async () => undefined,
  displayed() {}, open() {}, beginReview: () => () => {} });

export const useQualityWarning = () => useContext(Context);

export function QualityWarningProvider({ children, currentRouteName }: { children: ReactNode; currentRouteName?: string }) {
  const api = useApi(QualityWarningsClient);
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const queryClient = useQueryClient();
  const auth = useAppSelector(state => state.auth);
  const lightBox = useAppSelector(state => state.lightBox);
  const accountVisible = useAppSelector(state => state.myAccount.visible);
  const modalVisible = useAppSelector(state => state.modal.visible);
  const missionSeed = useAppSelector(state => state.missions.missionListSeed);
  const tracker = useRef(createQualityWarningSession()).current;
  const backend = Constants.expoConfig?.extra?.baseUrl ?? 'default';
  const accountKey = useCallback(() => {
    const current = store.getState().auth;
    return current.logged === LoggedState.Logged && current.idToken && current.user?.uid ? `${backend}:${current.user.uid}` : undefined;
  }, [backend, store]);
  const [session, setSession] = useState<WarningSession | undefined>(() => tracker.update(accountKey()));
  const [foreground, setForeground] = useState(AppState.currentState !== 'background' && AppState.currentState !== 'inactive' &&
    !(Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'hidden'));
  const [reviewCount, setReviewCount] = useState(0);
  const reviewCountRef = useRef(0);
  const owned = useRef<React.ReactElement | undefined>(undefined);
  const ownedWarningId = useRef<string | undefined>(undefined);
  const openedAcknowledged = useRef(false);
  const routeRef = useRef(currentRouteName);
  routeRef.current = currentRouteName;

  const closeOwned = useCallback(() => {
    if (owned.current && store.getState().lightBox.content === owned.current) {
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setOnClose(undefined));
      dispatch(lightBoxActions.setContent(<></>));
    }
    owned.current = undefined;
    ownedWarningId.current = undefined;
  }, [dispatch, store]);

  useEffect(() => {
    const update = () => {
      const next = tracker.update(accountKey());
      if (next !== session) setSession(next);
    };
    const unsubscribe = store.subscribe(update);
    update();
    return unsubscribe;
  }, [accountKey, session, store, tracker]);

  useEffect(() => {
    return () => {
      closeOwned();
      if (session) {
        void queryClient.cancelQueries({ queryKey: ['qualityWarning', session.key] });
        queryClient.removeQueries({ queryKey: ['qualityWarning', session.key] });
      }
    };
  }, [session, closeOwned, queryClient]);

  useEffect(() => {
    const update = () => setForeground(AppState.currentState !== 'background' && AppState.currentState !== 'inactive' &&
      !(Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'hidden'));
    const subscription = AppState.addEventListener('change', update);
    if (Platform.OS === 'web' && typeof document !== 'undefined') document.addEventListener('visibilitychange', update);
    return () => {
      subscription.remove();
      if (Platform.OS === 'web' && typeof document !== 'undefined') document.removeEventListener('visibilitychange', update);
    };
  }, []);

  const key = useMemo(() => ['qualityWarning', session?.key], [session]);
  const query = useQuery({ queryKey: key, enabled: !!session && !!auth.idToken && foreground,
    queryFn: () => api.getQualityWarning(), staleTime: 15_000, refetchInterval: foreground ? 60_000 : false,
    refetchIntervalInBackground: false, refetchOnWindowFocus: true, useErrorBoundary: false });
  const warning = tracker.isCurrent(session) ? query.data?.activeWarning : undefined;
  const warningRef = useRef(warning);
  warningRef.current = warning;
  const refresh = useCallback(async () => {
    if (!tracker.isCurrent(session)) return undefined;
    const result = await queryClient.fetchQuery({ queryKey: key, queryFn: () => api.getQualityWarning(), staleTime: 0 });
    if (!tracker.isCurrent(session)) throw new Error('Shopper session changed');
    return result.activeWarning;
  }, [api, key, queryClient, session, tracker]);

  const acknowledge = useCallback(async (id: string) => {
    if (!tracker.isCurrent(session)) throw new Error('Shopper session changed');
    await queryClient.cancelQueries({ queryKey: key });
    const response = await api.acknowledgeQualityWarning(id);
    if (!tracker.isCurrent(session)) throw new Error('Shopper session changed');
    queryClient.setQueryData(key, response);
    if (response.activeWarning?.id === id && !response.activeWarning.acknowledgedAtUtc) throw new Error('Warning was not acknowledged');
    return response.activeWarning;
  }, [api, key, queryClient, session, tracker]);

  const displayed = useCallback((id: string) => {
    if (!tracker.isCurrent(session)) return;
    tracker.present(id);
    if (warningRef.current?.id === id && warningRef.current.firstDisplayedAtUtc) return;
    void api.recordQualityWarningDisplay(id).then(() => {
      if (tracker.isCurrent(session)) void queryClient.invalidateQueries({ queryKey: key });
    }).catch(() => { /* The next visible presentation or refresh retries the idempotent receipt. */ });
  }, [api, key, queryClient, session, tracker]);

  const open = useCallback(() => {
    const active = warningRef.current;
    const current = store.getState().lightBox;
    if (!active?.id || !tracker.isCurrent(session) || reviewCountRef.current || current.visible || current.secondVisible || current.isLoading || store.getState().modal.visible) return;
    const content = <QualityWarningPopup warning={active} onClose={closeOwned}
      onDisplay={() => displayed(active.id!)} onAcknowledge={async () => {
        const latest = await acknowledge(active.id!);
        if (tracker.isCurrent(session) && (!latest || latest.id === active.id && latest.acknowledgedAtUtc)) closeOwned();
      }} />;
    owned.current = content;
    ownedWarningId.current = active.id;
    openedAcknowledged.current = !!active.acknowledgedAtUtc;
    dispatch(lightBoxActions.setOnClose(closeOwned));
    dispatch(lightBoxActions.setContent(content));
    dispatch(lightBoxActions.setVisibility(true));
  }, [acknowledge, closeOwned, dispatch, displayed, session, store, tracker]);

  const beginReview = useCallback(() => {
    reviewCountRef.current++;
    setReviewCount(reviewCountRef.current);
    closeOwned();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      reviewCountRef.current = Math.max(0, reviewCountRef.current - 1);
      setReviewCount(reviewCountRef.current);
    };
  }, [closeOwned]);

  useEffect(() => {
    if (!session || !foreground || !tracker.isCurrent(session)) return;
    void refresh().catch(() => {});
  }, [session, foreground, accountVisible, missionSeed]);

  useEffect(() => {
    if (warning?.id && !warning.firstDisplayedAtUtc && tracker.wasPresented(warning.id)) displayed(warning.id);
  }, [query.dataUpdatedAt, displayed, tracker]);

  useEffect(() => {
    if (owned.current && (!warning || warning.id !== ownedWarningId.current ||
      (!openedAcknowledged.current && !!warning.acknowledgedAtUtc) ||
      !['map', 'mymissions', 'myreferences', 'remotemissions'].includes(currentRouteName ?? ''))) closeOwned();
    if (!warning?.id || warning.acknowledgedAtUtc || tracker.wasPresented(warning.id) || !foreground || accountVisible || reviewCount ||
      !['map', 'mymissions', 'myreferences', 'remotemissions'].includes(currentRouteName ?? '') || modalVisible || lightBox.visible || lightBox.secondVisible || lightBox.isLoading) return;
    const timer = setTimeout(() => {
      if (tracker.isCurrent(session) && reviewCountRef.current === 0 && !tracker.wasPresented(warning.id!) && routeRef.current === currentRouteName) open();
    }, 600);
    return () => clearTimeout(timer);
  }, [accountVisible, closeOwned, currentRouteName, foreground, lightBox.visible, lightBox.secondVisible, lightBox.isLoading, modalVisible, open, reviewCount, session, tracker, warning]);

  return <Context.Provider value={{ sessionKey: session?.key, warning, refresh, acknowledge, displayed, open, beginReview }}>{children}</Context.Provider>;
}
